import { createFileRoute } from '@tanstack/react-router'
import { useReport } from '@/hooks/use-report'
import { useDeferredValue } from 'react'
import { noop, useSuspenseQuery } from '@tanstack/react-query'
import { trpc } from '@/main'
import { Distribution } from './-components/shared/distribution'
import { DistributionPending } from './-components/shared/distribution-pending'
import { useRootSearch } from '@/hooks/use-root-search'
import { PanelEmptyState } from './-components/shared/panel-empty-state'
import { UsersIcon } from 'lucide-react'
import { useLingui } from '@lingui/react/macro'

export const Route = createFileRoute('/_app/orgs/$orgId/groups/$groupId/distribution')({
  loaderDeps: ({ search }) => ({ orgType: search.orgType }),
  context: ({ deps, params }) => ({
    orgGroupProfileQueryOptions: trpc.orgs.getOrgGroupProfile.queryOptions({
      orgId: params.orgId,
      groupId: params.groupId,
      orgType: deps.orgType,
    }),
  }),
  loader: ({ context }) => {
    context.queryClient.query(context.orgGroupProfileQueryOptions).catch(noop)
  },
  component: RouteComponent,
  pendingComponent: DistributionPending,
  // Shown immediately: at the router's default pendingMs the NAV STACK cannot push while the loaders run,
  // because the level it would push is the route being rendered — see the full story in jobs.$jobId.report.
  pendingMs: 0,
  pendingMinMs: 0,
})

function RouteComponent() {
  const { t } = useLingui()
  const { second } = useRootSearch()

  if (!second?.id) {
    return (
      <PanelEmptyState
        icon={<UsersIcon className="text-muted-foreground size-5" />}
        title={t`Select a user`}
        description={t`Select a user from the list to view their profile.`}
      />
    )
  }

  return <DistributionLoaded />
}

function DistributionLoaded() {
  const context = Route.useRouteContext()
  const { report } = useReport(context.reportQueryOptions)
  // Deferred so switching ámbito keeps the PREVIOUS view on screen while the new data loads — the
  // options object comes from the route's context, so this is the very object the loader warmed.
  const deferredProfile = useDeferredValue(context.orgGroupProfileQueryOptions)
  const { data: orgGroupProfile } = useSuspenseQuery(deferredProfile)

  return (
    <Distribution report={report} profiles={orgGroupProfile?.profiles ?? []} data={orgGroupProfile?.distribution} />
  )
}
